import React, { useState, useEffect } from 'react';
import './V3Categories.scss';

interface Category {
  id: number;
  name: string;
  price: number;
  price_display?: string;
  description?: string;
  sold_out: boolean;
  remaining: number;
  hidden: boolean;
  cluster?: {
    id: number;
    name: string;
    description?: string;
    icon?: string;
    display_order: number;
  };
  tag?: string;
}

interface OrganizedCategories {
  unclustered: Category[];
  clustered: Array<{
    name: string;
    description?: string;
    icon?: string;
    price_range: string;
    categories: Category[];
  }>;
}

interface V3CategoriesProps {
  event: any;
  organizedCategories?: OrganizedCategories;
  loading?: boolean;
  error?: string | null;
}

const V3Categories: React.FC<V3CategoriesProps> = ({
  event,
  organizedCategories,
  loading = false,
  error = null
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Collapse all groups initially
    if (organizedCategories?.clustered) {
      const allGroupNames = new Set(organizedCategories.clustered.map(group => group.name));
      setCollapsedGroups(allGroupNames);
    }
  }, [organizedCategories]);

  const toggleCategoryGroup = (groupName: string) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const formatPrice = (category: Category) => {
    if (category.price === 0) {
      return 'Free';
    }
    const symbol = event?.setting?.currency?.symbol || '$';
    return category.price_display || `${symbol} ${category.price.toFixed(2)}`;
  };

  const getPriceDisplay = (category: Category) => {
    return formatPrice(category);
  };

  const getRegistrationUrl = (category: Category) => {
    // Use window.location for Django URLs since they're not handled by React Router
    return `/events/${event?.slug}/categories/${category.id}/register/`;
  };

  const handleCategoryClick = (e: React.MouseEvent, category: Category) => {
    e.preventDefault();
    const url = getRegistrationUrl(category);
    window.location.href = url;
  };

  const isSoldOut = (category: Category) => {
    return category.sold_out || category.remaining <= 0;
  };

  const getRemaining = (category: Category) => {
    return category.remaining || 0;
  };

  if (loading) {
    return (
      <div className="v3-categories">
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading categories...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v3-categories">
        <div className="alert alert-warning" role="alert">
          <strong>Categories Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!organizedCategories || (!organizedCategories.unclustered?.length && !organizedCategories.clustered?.length)) {
    return null;
  }

  return (
    <div className="v3-categories">
      {event?.setting?.live && (
        <a
          href={`/events/${event?.slug}/cluster-seated-registration/`}
          className="btn btn-primary cluster-selection-btn w-100 mb-3"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = `/events/${event?.slug}/cluster-seated-registration/`;
          }}
        >
          <i className="fas fa-ticket-alt"></i>
          <span>Automatically Pick My Seats</span>
          <i className="fas fa-arrow-right"></i>
        </a>
      )}

      <div className="categories-container">
        {/* Unclustered categories */}
        {organizedCategories.unclustered?.map((category, index) => {
          const soldOut = isSoldOut(category);
          const remaining = getRemaining(category);

          return (
            <div
              key={`unclustered-${category.id}-${index}`}
              className={`category-card ${soldOut ? 'sold-out' : ''}`}
            >
              {!event?.setting?.on_page_registration && !soldOut && (
                <a
                  href={getRegistrationUrl(category)}
                  onClick={(e) => handleCategoryClick(e, category)}
                  className="category-card-link"
                  style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <div className="category-header">
                    <div className="category-info">
                      <h4 className="category-name">{category.name}</h4>
                      <div className="price-container">
                        <span className="price-tag">{getPriceDisplay(category)}</span>
                      </div>
                    </div>
                  </div>

                  {category.description && (
                    <div className="category-description">
                      <p dangerouslySetInnerHTML={{ __html: category.description }} />
                    </div>
                  )}

                  {!event?.setting?.on_page_registration && (
                    <div className="category-footer">
                      {event?.setting?.show_remaining_passes && (
                        <span className="remaining-passes">
                          <i className="fas fa-ticket-alt"></i>
                          {remaining} Left
                        </span>
                      )}
                      <div className="category-action-btn">
                        <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  )}
                </a>
              )}

              {(!event?.setting?.on_page_registration || soldOut) && (
                <>
                  <div className="category-header">
                    <div className="category-info">
                      <h4 className="category-name">{category.name}</h4>
                      <div className="price-container">
                        <span className="price-tag">{getPriceDisplay(category)}</span>
                      </div>
                    </div>
                  </div>

                  {category.description && (
                    <div className="category-description">
                      <p dangerouslySetInnerHTML={{ __html: category.description }} />
                    </div>
                  )}

                  {!event?.setting?.on_page_registration && (
                    <div className="category-footer">
                      {event?.setting?.show_remaining_passes && (
                        <span className="remaining-passes">
                          <i className="fas fa-ticket-alt"></i>
                          {remaining} Left
                        </span>
                      )}
                      <div className="category-action-btn">
                        <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {/* Clustered categories */}
        {organizedCategories.clustered?.map((clusterData) => {
          const isCollapsed = collapsedGroups.has(clusterData.name);

          return (
            <div key={clusterData.name} className="category-group">
              <div
                className="category-group-header"
                onClick={() => toggleCategoryGroup(clusterData.name)}
              >
                <h5 className="category-group-title">
                  {clusterData.icon && <i className={`${clusterData.icon} me-2`}></i>}
                  {clusterData.name}
                </h5>
                {clusterData.description && (
                  <p className="category-group-description">{clusterData.description}</p>
                )}
                <div>
                  <span className="bg-primary rounded-pill px-2 py-1 me-2 text-white">
                    {clusterData.price_range}
                  </span>
                  <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'} category-group-icon`}></i>
                </div>
              </div>
              <div
                className="category-group-content"
                style={{ display: isCollapsed ? 'none' : 'block' }}
              >
                {clusterData.categories.map((category, catIndex) => {
                  const soldOut = isSoldOut(category);
                  const remaining = getRemaining(category);

                  return (
                    <div
                      key={`clustered-${clusterData.name}-${category.id}-${catIndex}`}
                      className={`category-card ${soldOut ? 'sold-out' : ''}`}
                    >
                      {!event?.setting?.on_page_registration && !soldOut && (
                        <a
                          href={getRegistrationUrl(category)}
                          onClick={(e) => handleCategoryClick(e, category)}
                          className="category-card-link"
                          style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit', display: 'block' }}
                        >
                          <div className="category-header">
                            <div className="category-info">
                              <h4 className="category-name">{category.name}</h4>
                              <div className="price-container">
                                <span className="price-tag">{getPriceDisplay(category)}</span>
                              </div>
                            </div>
                          </div>

                          {category.description && (
                            <div className="category-description">
                              <p dangerouslySetInnerHTML={{ __html: category.description }} />
                            </div>
                          )}

                          {!event?.setting?.on_page_registration && (
                            <div className="category-footer">
                              {event?.setting?.show_remaining_passes && (
                                <span className="remaining-passes">
                                  <i className="fas fa-ticket-alt"></i>
                                  {remaining} Left
                                </span>
                              )}
                              <div className="category-action-btn">
                                <i className="fas fa-arrow-right"></i>
                              </div>
                            </div>
                          )}
                        </a>
                      )}

                      {(!event?.setting?.on_page_registration || soldOut) && (
                        <>
                          <div className="category-header">
                            <div className="category-info">
                              <h4 className="category-name">{category.name}</h4>
                              <div className="price-container">
                                <span className="price-tag">{getPriceDisplay(category)}</span>
                              </div>
                            </div>
                          </div>

                          {category.description && (
                            <div className="category-description">
                              <p dangerouslySetInnerHTML={{ __html: category.description }} />
                            </div>
                          )}

                          {!event?.setting?.on_page_registration && (
                            <div className="category-footer">
                              {event?.setting?.show_remaining_passes && (
                                <span className="remaining-passes">
                                  <i className="fas fa-ticket-alt"></i>
                                  {remaining} Left
                                </span>
                              )}
                              <div className="category-action-btn">
                                <i className="fas fa-arrow-right"></i>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default V3Categories;

